import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Create axios instance with authorization header
const createAuthenticatedRequest = (token) => {
  return axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  });
};

// Submit a new report
export const submitReport = async (reportData, attachments, token) => {
  const req = createAuthenticatedRequest(token);
  
  // If there are attachments, use FormData to handle multipart form
  if (attachments && attachments.length > 0) {
    const formData = new FormData();
    
    // Add JSON fields
    Object.keys(reportData).forEach(key => {
      if (typeof reportData[key] === 'object' && reportData[key] !== null) {
        formData.append(key, JSON.stringify(reportData[key]));
      } else {
        formData.append(key, reportData[key]);
      }
    });
    
    // Add attachment files
    attachments.forEach(file => {
      formData.append('attachments', file);
    });
    
    // Submit with attachments
    return req.post('/reports', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
  }
  
  // Submit without attachments (simple JSON)
  return req.post('/reports', reportData);
};

// Get user's own reports
export const getUserReports = async (token, page = 1, limit = 10) => {
  const req = createAuthenticatedRequest(token);
  return req.get(`/reports/my-reports?page=${page}&limit=${limit}`);
};

// Get a specific report by ID
export const getReportById = async (reportId, token) => {
  const req = createAuthenticatedRequest(token);
  return req.get(`/reports/${reportId}`);
};

// Admin: Get all reports
export const getAllReports = async (token, filters = {}) => {
  const req = createAuthenticatedRequest(token);
  
  // Build query string from filters
  const queryParams = new URLSearchParams();
  if (filters.page) queryParams.append('page', filters.page);
  if (filters.limit) queryParams.append('limit', filters.limit);
  if (filters.status) queryParams.append('status', filters.status);
  if (filters.type) queryParams.append('type', filters.type);
  if (filters.priority) queryParams.append('priority', filters.priority);
  if (filters.search) queryParams.append('search', filters.search);
  
  const queryString = queryParams.toString();
  return req.get(`/reports${queryString ? '?' + queryString : ''}`);
};

// Admin: Update report status
export const updateReport = async (reportId, updateData, token) => {
  const req = createAuthenticatedRequest(token);
  return req.patch(`/reports/${reportId}`, updateData);
};

// Admin: Delete a report
export const deleteReport = async (reportId, token) => {
  const req = createAuthenticatedRequest(token);
  return req.delete(`/reports/${reportId}`);
};

// Utility function to get report type labels
export const getReportTypeLabel = (type) => {
  const labels = {
    bug: 'Bug/Technical Issue',
    incorrect_info: 'Incorrect Information',
    missing_content: 'Missing Content',
    feature_request: 'Feature Request',
    other: 'Other'
  };
  return labels[type] || 'Report';
};

// Utility function to get status labels
export const getStatusLabel = (status) => {
  const labels = {
    new: 'New',
    under_review: 'Under Review',
    in_progress: 'In Progress',
    fixed: 'Fixed',
    wont_fix: 'Won\'t Fix',
    duplicate: 'Duplicate',
    closed: 'Closed'
  };
  return labels[status] || status;
};

// Utility function to get priority labels
export const getPriorityLabel = (priority) => {
  const labels = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    critical: 'Critical'
  };
  return labels[priority] || priority;
};